import axios from '@/libs/axios'
import siteManager from './site-manager'
import AuthService from './auth-header'
import {
  prepareOfferHtmlForPdf,
  ensureOfferLetterChrome,
  ensureOfferPrintFlowSpacers,
  hardenOfferPdfForcedBreaks,
  parseOfferPageStartsMarker,
  splitSharedPrintFlowBeforePolicies,
  splitSharedPrintFlowBeforeSalary,
  applySalaryAnnexurePageStart,
  extractOfferImagesFromMetadata,
  extractOfferSignatureImageSrc,
  forceInjectOfferStampSrc,
  normalizeOfferLayout,
  normalizeOfferSignatureLocations,
  normalizeOfferEmployeeSignConfig,
  detectOfferEmployeeSignConfig,
  applyOfferLayoutToHtml,
  resolveOfferHeaderBannerSrc,
  parseOfferTemplate,
  buildOfferFieldsFromMetadata,
  repairOfferBodyAfterChrome,
  repairOfferSalaryForPdfFromDraft,
} from '@/utils/offer-template-editor'

let API_URL = ''
if (process.env.NODE_ENV === "production") {
  API_URL = 'https://ats.sattvahuman.in/api/';
} else {
  API_URL = 'http://192.168.0.30:5000/api/';
}

class OfferLetterService {
  // Convert image to base64
  async imageToBase64(imagePath) {
    try {
      const response = await fetch(imagePath)
      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error('Error converting image to base64:', error)
      return null
    }
  }

  // Helper function to extract only the name from candidate name (removes ID prefix like "SHC-61399 - ")
  extractCandidateName(fullName) {
    if (!fullName) return ''
    // Check if name contains " - " pattern (ID - NAME)
    const parts = fullName.split(' - ')
    if (parts.length > 1) {
      // Return everything after " - " (the actual name)
      return parts.slice(1).join(' - ').trim()
    }
    // If no " - " pattern, return the full name as is
    return fullName.trim()
  }

  // Helper function to convert SNAKE_CASE to camelCase
  // Example: OFFER_LETTER -> offerLetter, FIVE_METER_INSTALLATION -> fiveMeterInstallation
  toCamelCase(str) {
    return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase()).replace(/^[A-Z]/, (g) => g.toLowerCase())
  }

  // Helper function to get nested value from object using dot notation path
  // Handles both SNAKE_CASE (from HTML) and camelCase (from config JSON)
  // Example: getNestedValue(config, 'OFFER_LETTER.EN.TITLE') returns config.offerLetter.en.title
  getNestedValue(obj, path) {
    const keys = path.split('.')
    let value = obj

    for (const key of keys) {
      if (value === undefined || value === null) return ''

      // Handle array access like [0] or .0
      if (key.includes('[')) {
        const [arrayKey, index] = key.split('[')
        const idx = parseInt(index.replace(']', ''))
        const camelKey = this.toCamelCase(arrayKey)
        value = value?.[camelKey]?.[idx] || value?.[arrayKey]?.[idx]
      } else {
        // Try camelCase first (config JSON uses camelCase), then original key
        const camelKey = this.toCamelCase(key)
        value = value?.[camelKey] || value?.[key]
      }
    }

    return value !== undefined && value !== null ? String(value) : ''
  }

  // Helper function to replace all CONFIG.* placeholders with values from config object
  // Automatically finds {{CONFIG.X.Y.Z}} patterns and replaces them
  // Note: siteConfig from getOfferLetterConfig already has language-specific values extracted
  // So CONFIG.OFFER_LETTER.EN.TITLE maps to config.offerLetter.title (not config.offerLetter.en.title)
  replaceConfigPlaceholders(html, config) {
    let result = html

    // Find all {{CONFIG.X.Y.Z}} patterns
    const configRegex = /\{\{CONFIG\.([^}]+)\}\}/g

    result = result.replace(configRegex, (match, path) => {
      // Remove 'EN.' prefix since getOfferLetterConfig already extracts English values
      // CONFIG.OFFER_LETTER.EN.TITLE -> OFFER_LETTER.TITLE
      let cleanPath = path.replace(/^EN\./, '').replace(/\.EN\./g, '.')

      // Check if it ends with _FORMATTED - we need to format numbers
      const needsFormatting = cleanPath.endsWith('_FORMATTED')
      if (needsFormatting) {
        cleanPath = cleanPath.replace('_FORMATTED', '')
      }

      // Get the value from config using the path
      // The path will be converted from SNAKE_CASE to camelCase automatically
      // Example: OFFER_LETTER.TITLE -> config.offerLetter.title
      let value = this.getNestedValue(config, cleanPath)

      // Format numbers if needed
      if (needsFormatting && value) {
        const numValue = parseFloat(value)
        if (!isNaN(numValue)) {
          value = numValue.toLocaleString('en-IN')
        }
      }

      return value || ''
    })

    return result
  }

  // Helper function to handle conditional blocks {{#IF KEY}}...{{/IF}}
  replaceConditionalBlocks(html, config) {
    let result = html

    // Handle conditional blocks {{#IF KEY}}...{{/IF}}
    const conditionalRegex = /\{\{#IF\s+([^}]+)\}\}([\s\S]*?)\{\{\/IF\}\}/g
    result = result.replace(conditionalRegex, (match, conditionKey, content) => {
      // Check if the condition key exists in config and has a truthy value
      const conditionValue = this.getNestedValue(config, conditionKey.trim())
      if (conditionValue && conditionValue !== '' && conditionValue !== 'null' && conditionValue !== 'undefined' && conditionValue !== 'false') {
        // Condition is true, return the content
        return content
      } else {
        // Condition is false, remove the block
        return ''
      }
    })

    return result
  }

  // Generate offer letter HTML using database template
  // Note: All offer letters are generated in English only
  // All HTML/CSS must come from database - no hardcoded templates
  async generateOfferLetterHTML(candidate, offerDetails, siteCode = 'up', language = 'en', jobTitle = null) {
    // Force English language for all offer letters
    language = 'en'

    // Get site-specific configuration from database (will throw error if not found)
    // If jobTitle is provided, use getOfferLetterConfigByJobTitle, otherwise use default
    const siteConfig = jobTitle
      ? await siteManager.getOfferLetterConfigByJobTitle(siteCode, jobTitle, language)
      : await siteManager.getOfferLetterConfig(siteCode, language)

    // Validate that HTML template exists in database
    if (!siteConfig.html || typeof siteConfig.html !== 'string') {
      throw new Error(`HTML template not found in database for site: ${siteCode} and language: ${language}. Please ensure the HTML template is configured in the database.`)
    }

    console.log('Using site configuration for:', siteCode, 'Language:', language)
    console.log('HTML template found in database:', siteConfig.html ? 'Yes' : 'No')

    // Extract only the name part from candidateName (remove ID prefix)
    const candidateNameOnly = this.extractCandidateName(offerDetails.candidateName)

    // Helper function to check if a string is already a base64 data URL
    const isBase64DataUrl = (str) => {
      if (!str || typeof str !== 'string') return false
      return str.startsWith('data:image/') || str.startsWith('data:application/')
    }

    // Helper function to load image - use base64 directly if available, otherwise convert from URL
    const loadImage = async (imageValue) => {
      if (!imageValue) return null

      // If already base64 data URL, use it directly
      if (isBase64DataUrl(imageValue)) {
        console.log('Image is already base64, using directly')
        return imageValue
      }

      // Otherwise, try to convert from URL/path to base64
      try {
        return await this.imageToBase64(imageValue)
      } catch (error) {
        console.error('Error converting image to base64:', error)
        return null
      }
    }

    // Resolve branding images (logo/companyLogo + watermark casing variants)
    const resolvedImages = extractOfferImagesFromMetadata(siteConfig.metadata)
    // Saved templates often keep only {{METADATA.IMAGES.SIGNATURE}} in HTML and the
    // real stamp in metadata.images — but some records inline the URL/base64 in HTML
    // while metadata.images is empty (list truncates / backend omits large fields).
    const stampFromHtml = extractOfferSignatureImageSrc(siteConfig.html || '', {
      ...resolvedImages,
      images: resolvedImages,
      signature: resolvedImages.signature,
      stamp: resolvedImages.signature,
    })
    const imagePaths = {
      watermark: resolvedImages.watermark,
      signature: resolvedImages.signature || stampFromHtml || '',
      companyLogo: resolvedImages.logo,
      header: resolvedImages.header,
    }

    console.log('Loading images for offer letter...')
    console.log('Image paths from metadata:', {
      watermark: imagePaths.watermark ? (isBase64DataUrl(imagePaths.watermark) ? 'Base64 (already encoded)' : imagePaths.watermark) : 'Not provided',
      signature: imagePaths.signature ? (isBase64DataUrl(imagePaths.signature) ? 'Base64 (already encoded)' : imagePaths.signature) : 'Not provided',
      companyLogo: imagePaths.companyLogo ? (isBase64DataUrl(imagePaths.companyLogo) ? 'Base64 (already encoded)' : imagePaths.companyLogo) : 'Not provided',
      header: imagePaths.header ? (isBase64DataUrl(imagePaths.header) ? 'Base64 (already encoded)' : imagePaths.header) : 'Not provided'
    })
    if (!imagePaths.signature) {
      console.warn(
        '[Offer Letter Service] No stamp URL in metadata.images.signature or template HTML. '
        + 'Offer Configure must save metadata.images.signature (base64 or https URL) for the stamp to appear in generated PDFs.',
      )
    }

    // Load images (will use base64 directly if available, or convert from URL)
    const watermarkLogo = await loadImage(imagePaths.watermark)
    const signatureStamp = await loadImage(imagePaths.signature)
    const companyLogo = await loadImage(imagePaths.companyLogo)
    const headerImg = await loadImage(imagePaths.header)

    console.log('Image loading results:')
    console.log('Header image loaded:', headerImg ? 'Yes' : 'No', headerImg ? `(${headerImg.substring(0, 50)}...)` : '')
    console.log('Company logo loaded:', companyLogo ? 'Yes' : 'No', companyLogo ? `(${companyLogo.substring(0, 50)}...)` : '')
    console.log('Watermark loaded:', watermarkLogo ? 'Yes' : 'No', watermarkLogo ? `(${watermarkLogo.substring(0, 50)}...)` : '')
    console.log('Signature loaded:', signatureStamp ? 'Yes' : 'No', signatureStamp ? `(${signatureStamp.substring(0, 50)}...)` : '')

    // Format date in English locale (all offer letters are in English)
    const formatDate = (dateString) => {
      if (!dateString) return ''
      const locale = 'en-IN'
      return new Date(dateString).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    // Helper function to safely format numbers (English locale)
    const formatNumber = (value) => {
      if (value === null || value === undefined || isNaN(value)) {
        return '0'
      }
      const locale = 'en-IN'
      return Number(value).toLocaleString(locale)
    }

    // Start with HTML template from database; reinject fixed header/footer/watermark
    // chrome if a prior Offer Configure save dropped production letter shell pieces.
    const meta = siteConfig.metadata || {}
    // Prefer inlined base64 for PDF; keep the stored URL when fetch/CORS fails so
    // Puppeteer can still load it — never drop a usable stamp src.
    const stampForChrome = signatureStamp || imagePaths.signature || stampFromHtml || ''
    let html = prepareOfferHtmlForPdf(siteConfig.html || '', {
      ...meta,
      images: {
        ...resolvedImages,
        signature: stampForChrome,
        header: headerImg || resolvedImages.header || '',
        logo: companyLogo || resolvedImages.logo || '',
        watermark: watermarkLogo || resolvedImages.watermark || '',
      },
      signatureSrc: stampForChrome,
      signature: stampForChrome,
      stamp: stampForChrome,
    })

    // Step 1: Replace images (METADATA.IMAGES.*) — keep WATERMARK token casing
    html = html.replace(/\{\{\s*METADATA\.IMAGES\.HEADER\s*\}\}/gi, resolveOfferHeaderBannerSrc(headerImg))
    html = html.replace(/\{\{\s*METADATA\.IMAGES\.LOGO\s*\}\}/gi, companyLogo || '')
    if (watermarkLogo) {
      html = html.replace(/\{\{\s*METADATA\.IMAGES\.WATERMARK\s*\}\}/gi, watermarkLogo)
    } else {
      // Empty src paints alt text ("Watermark Logo") into the PDF — remove the node.
      html = html.replace(/<img[^>]*class\s*=\s*["'][^"']*\bwatermark\b[^"']*["'][^>]*>\s*/gi, '')
      html = html.replace(/\{\{\s*METADATA\.IMAGES\.WATERMARK\s*\}\}/gi, '')
    }
    if (stampForChrome) {
      // Function replacer avoids $-patterns in base64/URLs being treated as special.
      html = html.replace(/\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}/gi, () => stampForChrome)
      // Harden: force every stamp/signature img (any attr order) + create section if needed.
      html = forceInjectOfferStampSrc(html, stampForChrome)
    } else {
      // No stamp configured — drop empty/token signature images only.
      html = html.replace(
        /<img[^>]*class\s*=\s*["'][^"']*\b(?:signature-img|stamp-img)\b[^"']*["'][^>]*src\s*=\s*["']\s*(?:\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\})?\s*["'][^>]*>\s*/gi,
        '',
      )
      html = html.replace(/\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}/gi, '')
    }

    // Step 2: Replace only candidate-specific dynamic fields (6 fields only)
    html = html.replace(/\{\{CANDIDATE\.NAME_ONLY\}\}/g, candidateNameOnly)
    html = html.replace(/\{\{CANDIDATE\.NAME\}\}/g, candidateNameOnly)
    html = html.replace(/\{\{OFFER_DETAILS\.OFFERED_DATE_FORMATTED\}\}/g, formatDate(offerDetails.offeredDate))
    html = html.replace(/\{joiningDate\}/g, formatDate(offerDetails.joiningDate))
    html = html.replace(/\{reportingManager\}/g, offerDetails.reportingManagerName || 'N/A')
    html = html.replace(/\{jobTitle\}/g, candidate.position_name || 'N/A')
    html = html.replace(/\{locations\}/g, offerDetails.location || 'N/A')

    console.log('✅ [Offer Letter Service] Replacement completed')
    console.log('✅ [Offer Letter Service] Only 6 candidate-specific fields and images replaced (no CONFIG.* replacements needed)')

    return html
  }

  // Generate offer letter PDF (simplified version for browser)
  // Note: All offer letters are generated in English only
  // HTML template from database should already be a complete HTML document
  async generateOfferLetterPDF(candidate, offerDetails, siteCode = 'up', language = 'en') {
    // Force English language for all offer letters
    language = 'en'
    try {
      // Get complete HTML from database (should include DOCTYPE, html, head, body tags)
      const html = await this.generateOfferLetterHTML(candidate, offerDetails, siteCode, language)

      // Create a new window with the offer letter content
      // HTML from database should already be a complete document
      const printWindow = window.open('', '_blank')
      printWindow.document.write(html)
      printWindow.document.close()

      // Wait for content to load, then print
      printWindow.onload = function () {
        printWindow.print()
        // Don't close immediately, let user decide
      }

      return { success: true, message: 'offer letter opened for printing/download', html }
    } catch (error) {
      console.error('Error generating offer letter PDF:', error)
      return { success: false, message: 'Failed to generate offer letter PDF' }
    }
  }

  // Generate offer letter HTML and return it (for download/email)
  // Note: All offer letters are generated in English only
  async generateOfferLetterHTMLForDownload(candidate, offerDetails, siteCode = 'up', language = 'en', jobTitle = null) {
    // Force English language for all offer letters
    language = 'en'
    try {
      const html = await this.generateOfferLetterHTML(candidate, offerDetails, siteCode, language, jobTitle)

      // The HTML from database already includes all styling, just return it
      return { success: true, html: html }
    } catch (error) {
      console.error('Error generating offer letter HTML:', error)
      return { success: false, message: `Failed to generate offer letter for ${candidate.candidateName}` }
    }
  }

  // Get site options for dropdown
  async getSiteOptions(clientFilter = null) {
    return await siteManager.getSiteOptions(clientFilter)
  }

  // Get language options for dropdown
  getLanguageOptions() {
    return siteManager.getLanguageOptions()
  }

  /**
   * Generate PDF from HTML and save to S3.
   * Production API (SattvaATS generateOfferLetterPDF) requires truthy:
   *   { html, filename, candidate_id, job_id }
   * Response shape: { status, data: { base64, filename, s3Key, ... } }
   *
   * @param {object} [options]
   * @param {boolean} [options.preview] - template preview: send sentinel IDs (-1)
   *   so validation passes without updating a real candidate_jobs row.
   */
  async generatePDFFromHTML(html, filename, candidateId, jobId, options = {}) {
    if (!html || typeof html !== 'string' || !html.trim()) {
      throw new Error('Offer letter HTML is empty. Cannot generate PDF.')
    }
    if (!filename || typeof filename !== 'string' || !filename.trim()) {
      throw new Error('PDF filename is required.')
    }

    // Final chrome pass — catches PDF-16-style templates that still have
    // @page { margin: 130px … } + fixed top:0 (banner parked at ~y=97pt).
    // Layout CSS (title font/center) is parked+restored inside ensureOfferLetterChrome.
    // Prefer embedded OFFER_SIGNATURE_PLACEMENT marker (supports withClosing+afterTerms).
    // Pass it explicitly so the final chrome pass cannot fall back to a leftover last page.
    const inMarker = String(html).match(/<!--\s*OFFER_SIGNATURE_PLACEMENT:([\w,]+)\s*-->/i)
    const placementFromMarker = inMarker && inMarker[1]
      ? normalizeOfferSignatureLocations(inMarker[1])
      : null
    // Prefer embedded employee-sign marker so unchecked Name/Signature lines stay off
    // after the final PDF chrome pass (defaults would otherwise re-inject them).
    const employeeFromOptions = options.offerEmployeeSignConfig != null
      || options.offerEmployeeSignPlacement != null
      ? normalizeOfferEmployeeSignConfig(
        options.offerEmployeeSignConfig != null
          ? options.offerEmployeeSignConfig
          : options.offerEmployeeSignPlacement,
      )
      : null
    // Capture inlined stamp src BEFORE chrome — ensureOfferLetterChrome used to
    // rebuild signature blocks with src="{{METADATA.IMAGES.SIGNATURE}}", wiping
    // base64/http. Chrome now preserves src when found; reinject remains a safety net.
    const sigSrc = extractOfferSignatureImageSrc(html, {
      ...options,
      signatureSrc: options.signatureSrc || options.signature || options.stamp,
      signature: options.signature || options.signatureSrc || options.stamp,
      stamp: options.stamp || options.signature,
      images: options.images,
    })

    const chromeOpts = {}
    // Explicit Offer Configure options win over embedded markers (stale HTML).
    if (options.offerSignaturePlacement != null && options.offerSignaturePlacement !== '') {
      chromeOpts.offerSignaturePlacement = normalizeOfferSignatureLocations(
        options.offerSignaturePlacement,
      )
    } else if (placementFromMarker) {
      chromeOpts.offerSignaturePlacement = placementFromMarker
    }
    chromeOpts.offerEmployeeSignPlacement = detectOfferEmployeeSignConfig(
      html,
      employeeFromOptions,
    )
    if (sigSrc) {
      chromeOpts.signatureSrc = sigSrc
      chromeOpts.signature = sigSrc
      chromeOpts.images = { ...(options.images || {}), signature: sigSrc }
    }

    const parsedForRepair = parseOfferTemplate(html)
    const startsFromHtml = parseOfferPageStartsMarker(html)
    const repairFields = buildOfferFieldsFromMetadata(
      {
        ...options,
        offerLayout: options.offerLayout || startsFromHtml || options.offerLayout,
      },
      parsedForRepair.fields || {},
      html,
    )
    // generateOfferLetterHTML already ran prepareOfferHtmlForPdf → chrome.
    // A second full chrome+unwrap turned leftover after-break tables into
    // unmarked page breaks (empty Transportation page / 157 letterheads).
    const alreadyChromed = /offer-print-flow/i.test(html)
      && /<!--\s*OFFER_SIGNATURE_PLACEMENT:/i.test(html)
    let pdfHtml = alreadyChromed ? html : ensureOfferLetterChrome(html, chromeOpts)
    if (sigSrc) {
      pdfHtml = pdfHtml.replace(/\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}/gi, () => sigSrc)
      pdfHtml = forceInjectOfferStampSrc(pdfHtml, sigSrc)
    }
    pdfHtml = pdfHtml.replace(
      /<img[^>]*class\s*=\s*["'][^"']*\bwatermark\b[^"']*["'][^>]*src\s*=\s*["']\s*["'][^>]*>\s*/gi,
      '',
    )
    // Second chrome can drop OFFER_BODY / salary (Offer Configure already repairs this).
    const assembledHtml = pdfHtml
    try {
      pdfHtml = repairOfferBodyAfterChrome(pdfHtml, repairFields)
      pdfHtml = repairOfferSalaryForPdfFromDraft(pdfHtml, repairFields, options)
      pdfHtml = applySalaryAnnexurePageStart(pdfHtml, repairFields)
      pdfHtml = ensureOfferPrintFlowSpacers(pdfHtml)
      pdfHtml = splitSharedPrintFlowBeforePolicies(pdfHtml)
      pdfHtml = splitSharedPrintFlowBeforeSalary(pdfHtml)
      // Last mile: one page-break on the Policies/Salary TABLE after a named
      // Starts: New page marker (sibling table, not a cell). Empty
      // offer-page-break / force-break spacers must not also break (blank A4).
      pdfHtml = hardenOfferPdfForcedBreaks(pdfHtml)
    } catch (err) {
      console.warn('[Offer PDF] repair failed, using assembled HTML', err)
      pdfHtml = assembledHtml
    }
    const letterLooksEmpty = !pdfHtml
      || !/<(?:p|td|h[1-6]|div)[^>]*>[\s\S]{8,}/i.test(
        String(pdfHtml).replace(/<style[\s\S]*?<\/style>/gi, ''),
      )
    if (letterLooksEmpty && assembledHtml && assembledHtml.trim()) {
      console.warn('[Offer PDF] repair emptied letter, using assembled HTML')
      pdfHtml = assembledHtml
    }
    const bodyStart = (repairFields.offerLayout
      && repairFields.offerLayout.sections
      && repairFields.offerLayout.sections.body
      && repairFields.offerLayout.sections.body.pageStartMode) || 'unknown'
    const salaryStart = (repairFields.offerLayout
      && repairFields.offerLayout.sections
      && repairFields.offerLayout.sections.salary
      && repairFields.offerLayout.sections.salary.pageStartMode) || 'unknown'
    console.log('[Offer PDF] starts applied', {
      source: repairFields._offerLayoutSource || (startsFromHtml ? 'marker' : 'default'),
      bodyPageStartMode: bodyStart,
      salaryPageStartMode: salaryStart,
      hasPoliciesBreak: /OFFER_POLICIES_PAGE_BREAK/i.test(pdfHtml),
      hasSalaryBreak: /<!--\s*OFFER_PAGE_BREAK\s*-->/i.test(pdfHtml),
      policiesTableForced: /offer-print-flow-after-break/i.test(pdfHtml)
        && /Employment Policies and Provisions/i.test(pdfHtml),
      salaryTableForced: /offer-print-flow-after-break/i.test(pdfHtml)
        && /(?:Annexure\s+A|Salary\s+Structure)/i.test(pdfHtml),
    })

    // Base64-embedded images can push payloads past typical body limits.
    const htmlBytes = pdfHtml.length
    const MAX_HTML_CHARS = 2 * 1024 * 1024 // 2 MB client guard for text + modest images
    if (htmlBytes > MAX_HTML_CHARS) {
      throw new Error(
        `Offer letter HTML is too large (${(htmlBytes / (1024 * 1024)).toFixed(1)} MB; limit 2 MB). `
        + 'Reduce embedded image sizes (logo, header, watermark)—use compressed JPEG/PNG and try again.',
      )
    }

    // Backend rejects missing/0 IDs: `if (!req.body.candidate_id || !req.body.job_id)`.
    // Preview uses -1 (truthy, no matching candidate_jobs → warn only, PDF still returned).
    const PREVIEW_SENTINEL_ID = -1
    let resolvedCandidateId = this._normalizePdfEntityId(candidateId)
    let resolvedJobId = this._normalizePdfEntityId(jobId)
    if (options.preview) {
      if (resolvedCandidateId == null) resolvedCandidateId = PREVIEW_SENTINEL_ID
      if (resolvedJobId == null) resolvedJobId = PREVIEW_SENTINEL_ID
    }
    if (resolvedCandidateId == null || resolvedJobId == null) {
      throw new Error(
        'candidate_id and job_id are required by /offer-table-config/generate-pdf '
        + '(use preview: true for template-only PDF).',
      )
    }

    const payload = {
      html: pdfHtml,
      filename: filename.trim(),
      candidate_id: resolvedCandidateId,
      job_id: resolvedJobId,
    }

    try {
      const response = await axios.post(
        `${API_URL}offer-table-config/generate-pdf`,
        payload,
        {
          headers: AuthService.authHeader(),
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        },
      )
      const normalized = this._normalizePdfResponse(response.data)
      console.log(
        'PDF generated successfully, S3 key:',
        normalized.generateKey || normalized.filename || normalized.s3Key,
      )
      return normalized
    } catch (error) {
      const serverMsg = this._extractPdfErrorMessage(error)
      console.error(
        'Error generating PDF from HTML:',
        serverMsg,
        error?.response?.status,
        error?.response?.data,
      )
      const enriched = new Error(serverMsg)
      enriched.status = error?.response?.status
      enriched.response = error?.response
      enriched.responseData = error?.response?.data
      throw enriched
    }
  }

  _normalizePdfEntityId(value) {
    if (value == null || value === '') return null
    // 0 / "0" are falsy on the backend (`!req.body.candidate_id`) → 400
    if (typeof value === 'number' && (!Number.isFinite(value) || value === 0)) return null
    if (typeof value === 'string' && (!value.trim() || value.trim() === '0')) return null
    return value
  }

  _normalizePdfResponse(raw) {
    if (!raw || typeof raw !== 'object') return raw
    const nested = raw.data && typeof raw.data === 'object' ? raw.data : null
    const deeper = nested?.data && typeof nested.data === 'object' ? nested.data : null
    const base64 = nested?.base64 || deeper?.base64 || raw.base64
      || nested?.pdf || deeper?.pdf || raw.pdf
      || nested?.pdfBase64 || deeper?.pdfBase64 || raw.pdfBase64
      || nested?.file || deeper?.file || raw.file
      || null
    const url = nested?.s3Location || deeper?.s3Location || raw.s3Location
      || nested?.url || deeper?.url || raw.url
      || nested?.s3Url || deeper?.s3Url || raw.s3Url
      || nested?.offerLetterLocation || deeper?.offerLetterLocation || raw.offerLetterLocation
      || null
    // Prefer inline base64; fall back to S3/http URL (common when API omits base64).
    const payload = base64 || url || null
    const filename = nested?.filename || deeper?.filename || raw.filename || null
    const s3Key = nested?.s3Key || deeper?.s3Key || raw.s3Key || filename || null
    const loggedKey = [filename, s3Key].find((value) => {
      if (!value || typeof value !== 'string') return false
      const key = value.trim().replace(/^\/+/, '')
      return key && !this.isHttpOrBlobUrl(key) && !/^data:/i.test(key)
        && !this.isForbiddenOfferLetterMailKey(key)
    })
    const generateKey = loggedKey ? String(loggedKey).trim().replace(/^\/+/, '') : null
    return {
      ...raw,
      pdf: payload,
      pdfBase64: payload,
      url: url || null,
      s3Location: url || null,
      filename: filename || s3Key,
      s3Key,
      generateKey,
      data: nested
        ? {
          ...nested,
          base64: base64 || nested.base64,
          pdf: payload,
          pdfBase64: payload,
          url,
          s3Location: url,
          s3Key,
          generateKey,
        }
        : raw.data,
    }
  }

  /**
   * Pull PDF bytes/URL from generate-pdf or jobs/file/base64 response shapes.
   * Production generate-pdf: { status, data: { base64, filename, s3Key, s3Location } }
   * jobs/file/base64: { status, file }
   */
  extractPdfPayload(source) {
    if (source == null) return null
    if (typeof source === 'string' || (typeof Blob !== 'undefined' && source instanceof Blob)
      || (typeof ArrayBuffer !== 'undefined' && source instanceof ArrayBuffer)
      || (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(source))) {
      return source
    }
    if (typeof source !== 'object') return null

    const nested = source.data && typeof source.data === 'object' ? source.data : null
    const deeper = nested?.data && typeof nested.data === 'object' ? nested.data : null
    // content first, then URL fallbacks (never treat filename/s3Key as bytes)
    const content = source.pdfBase64 || source.pdf || source.base64 || source.file
      || nested?.pdfBase64 || nested?.pdf || nested?.base64 || nested?.file
      || deeper?.pdfBase64 || deeper?.pdf || deeper?.base64 || deeper?.file
      || null
    if (content) return content

    if (typeof source.data === 'string' && source.data.trim()) return source.data

    return source.s3Location || source.url || source.s3Url || source.offerLetterLocation
      || nested?.s3Location || nested?.url || nested?.s3Url || nested?.offerLetterLocation
      || deeper?.s3Location || deeper?.url || deeper?.s3Url || deeper?.offerLetterLocation
      || null
  }

  _pdfPayloadPreview(payload) {
    const t = typeof payload
    if (payload == null) return `type=${t}`
    if (t !== 'string') {
      const name = payload.constructor && payload.constructor.name
      const size = typeof payload.byteLength === 'number'
        ? payload.byteLength
        : (typeof payload.size === 'number' ? payload.size : undefined)
      return `type=${t}${name ? `(${name})` : ''}${size != null ? ` size=${size}` : ''}`
    }
    const head = payload.trim().slice(0, 80).replace(/\s+/g, ' ')
    return `type=string len=${payload.length} head=${JSON.stringify(head)}`
  }

  _byteArrayToPdfBlob(byteArray) {
    return new Blob([byteArray], { type: 'application/pdf' })
  }

  _parseDecimalByteList(compact) {
    const parts = compact.split(',')
    const byteArray = new Uint8Array(parts.length)
    for (let i = 0; i < parts.length; i += 1) {
      const n = Number(parts[i])
      if (!Number.isInteger(n) || n < 0 || n > 255) {
        throw new Error(`Invalid byte in PDF payload at index ${i}: ${parts[i]}`)
      }
      byteArray[i] = n
    }
    return byteArray
  }

  /**
   * Decode PDF payload to a Blob.
   * Supports: Blob, ArrayBuffer/TypedArray, numeric byte arrays,
   * comma-separated decimal byte strings, data-URL base64, raw/url-safe base64
   * (whitespace stripped), %PDF binary strings, blob:/http(s)/protocol-relative URLs.
   */
  async pdfPayloadToBlob(payload) {
    if (payload == null || payload === '') {
      throw new Error(`Empty PDF payload (${this._pdfPayloadPreview(payload)})`)
    }
    if (typeof Blob !== 'undefined' && payload instanceof Blob) return payload
    if (typeof ArrayBuffer !== 'undefined' && payload instanceof ArrayBuffer) {
      return this._byteArrayToPdfBlob(payload)
    }
    if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(payload)) {
      return this._byteArrayToPdfBlob(payload)
    }
    if (Array.isArray(payload)) {
      return this._byteArrayToPdfBlob(new Uint8Array(payload))
    }
    if (typeof payload !== 'string') {
      throw new Error(`Unsupported PDF payload (${this._pdfPayloadPreview(payload)})`)
    }

    const trimmed = payload.trim()
    if (!trimmed) {
      throw new Error(`Empty PDF payload (${this._pdfPayloadPreview(payload)})`)
    }

    // Fetchable URL (blob:, http(s):, or protocol-relative //)
    if (/^(blob:|https?:)/i.test(trimmed) || /^\/\//.test(trimmed)) {
      const url = /^\/\//.test(trimmed) ? `https:${trimmed}` : trimmed
      const res = await fetch(url)
      if (!res.ok) throw new Error(`Failed to fetch PDF URL (${res.status})`)
      return res.blob()
    }

    // Raw PDF binary string
    if (trimmed.startsWith('%PDF')) {
      const bytes = new Uint8Array(trimmed.length)
      for (let i = 0; i < trimmed.length; i += 1) {
        bytes[i] = trimmed.charCodeAt(i) & 0xff
      }
      return this._byteArrayToPdfBlob(bytes)
    }

    // JSON byte array string: "[37,80,68,70,...]"
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed) && parsed.length) {
          return this._byteArrayToPdfBlob(new Uint8Array(parsed))
        }
      } catch (err) {
        /* fall through */
      }
    }

    // Comma-separated decimal bytes (e.g. Node Buffer default toString: "37,80,68,70,...")
    if (/^\d{1,3},/.test(trimmed)) {
      const compact = trimmed.replace(/\s/g, '')
      if (/^[\d,]+$/.test(compact)) {
        return this._byteArrayToPdfBlob(this._parseDecimalByteList(compact))
      }
    }

    // S3 key / filename mistaken for bytes — do not atob
    if (/\.pdf$/i.test(trimmed) && !/^[A-Za-z0-9+/_=-]+$/.test(trimmed.replace(/\s/g, ''))) {
      throw new Error(
        `PDF payload looks like a file path/key, not base64 (${this._pdfPayloadPreview(trimmed)})`,
      )
    }

    let base64 = trimmed
    const dataUrlIdx = trimmed.indexOf('base64,')
    if (/^data:/i.test(trimmed) && dataUrlIdx !== -1) {
      base64 = trimmed.slice(dataUrlIdx + 7)
    }

    // Strip whitespace and URL-safe → standard base64
    base64 = base64.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/')
    if (!base64) {
      throw new Error(`Empty PDF base64 payload (${this._pdfPayloadPreview(payload)})`)
    }

    // Soft check: allow standard alphabet; reject obvious garbage before atob
    if (/[^A-Za-z0-9+/=]/.test(base64)) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('PDF payload is not valid base64:', this._pdfPayloadPreview(payload))
      }
      throw new Error(`PDF payload is not valid base64 (${this._pdfPayloadPreview(payload)})`)
    }

    // Pad to multiple of 4
    const pad = base64.length % 4
    if (pad) base64 += '='.repeat(4 - pad)

    try {
      const byteCharacters = atob(base64)
      const byteArray = new Uint8Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteArray[i] = byteCharacters.charCodeAt(i)
      }
      return this._byteArrayToPdfBlob(byteArray)
    } catch (err) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('Failed to decode PDF base64:', this._pdfPayloadPreview(payload), err)
      }
      throw new Error(
        `Failed to decode PDF base64: ${err.message || err} (${this._pdfPayloadPreview(payload)})`,
      )
    }
  }

  /**
   * Exact storage key from generate-pdf — no folder prefix, no OFFERLETTER- strip.
   * generate-pdf logs this as e.g. OFFERLETTER-DALVIR-KUMAR-mohali.pdf
   */
  captureGeneratePdfStorageKey(pdfResult) {
    if (!pdfResult || typeof pdfResult !== 'object') return null
    const nested = pdfResult.data && typeof pdfResult.data === 'object' ? pdfResult.data : null
    const deeper = nested?.data && typeof nested.data === 'object' ? nested.data : null
    // Same value as the generate-pdf success log (exact string, no rewrite)
    const candidates = [
      pdfResult.generateKey, nested?.generateKey, deeper?.generateKey,
      nested?.filename, deeper?.filename, pdfResult.filename,
      nested?.s3Key, deeper?.s3Key, pdfResult.s3Key,
    ]
    for (let i = 0; i < candidates.length; i += 1) {
      const raw = candidates[i]
      if (!raw || typeof raw !== 'string') continue
      const key = raw.trim().replace(/^\/+/, '')
      if (!key || this.isHttpOrBlobUrl(key) || /^data:/i.test(key)) continue
      if (this.isForbiddenOfferLetterMailKey(key)) continue
      return key
    }
    return null
  }

  /**
   * Folder + exact generate filename. NEVER strips OFFERLETTER- from the leaf
   * (do not invent OFFERLETTER/DALVIR-KUMAR-mohali.pdf from
   * OFFERLETTER-DALVIR-KUMAR-mohali.pdf).
   */
  withOfferLetterFolderPrefix(filenameOrKey) {
    if (!filenameOrKey || typeof filenameOrKey !== 'string') return null
    const key = filenameOrKey.trim().replace(/^\/+/, '')
    if (!key) return null
    if (this.isHttpOrBlobUrl(key) || /^data:/i.test(key)) return null
    if (this.isForbiddenOfferLetterMailKey(key)) return null
    if (/[\\/]/.test(key)) return key.replace(/\\/g, '/')
    return `OFFERLETTER/${key}`
  }

  /** Normalize OFFERLETTER S3 keys. Never strip the OFFERLETTER- filename prefix. */
  resolveOfferLetterS3Key(filenameOrKey) {
    return this.withOfferLetterFolderPrefix(filenameOrKey)
  }

  parseExtraDetails(source) {
    if (!source) return null
    if (typeof source === 'object') return source
    if (typeof source !== 'string') return null
    try {
      const parsed = JSON.parse(source)
      return parsed && typeof parsed === 'object' ? parsed : null
    } catch (err) {
      return null
    }
  }

  isHttpOrBlobUrl(value) {
    return /^(https?:|blob:|\/\/)/i.test(String(value || '').trim())
  }

  /** Strip ATS list label "SHC-369212 - RAKESH KUMAR TIWARI" → "RAKESH KUMAR TIWARI". */
  stripCandidateDisplayPrefix(name) {
    if (!name || typeof name !== 'string') return ''
    const trimmed = name.trim()
    const match = trimmed.match(/^SHC-\d+\s+-\s+(.+)$/i)
    return match ? match[1].trim() : trimmed
  }

  sanitizeOfferLetterFilenamePart(value) {
    return this.stripCandidateDisplayPrefix(value || '')
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }

  /**
   * Display/download names like "SHC-369212 - RAKESH KUMAR TIWARI-Mohali.pdf"
   * are not generate-pdf S3 keys (those are OFFERLETTER/OFFERLETTER-{name}-{loc}.pdf).
   */
  isDisplayStyleOfferFilename(value) {
    const name = String(value || '').trim().replace(/^\/+/, '').split('/').pop() || ''
    if (!name) return false
    if (/^OFFERLETTER-/i.test(name)) return false
    if (/SHC-\d+/i.test(name)) return true
    return /^SHC-\d+\s*-\s*/i.test(name)
  }

  /**
   * mail-hr strips one leading OFFERLETTER- from a slashed key
   * (OFFERLETTER/OFFERLETTER-X.pdf → OFFERLETTER/X.pdf 400).
   * Prepend one extra OFFERLETTER- so the lookup lands on the stored object.
   */
  toMailHrCompensatingKey(verifiedKey) {
    if (!verifiedKey || typeof verifiedKey !== 'string') return ''
    const key = verifiedKey.trim().replace(/^\/+/, '').replace(/\\/g, '/')
    if (!key || this.isForbiddenOfferLetterMailKey(key)) return ''
    const leaf = key.split('/').pop() || key
    if (/^OFFERLETTER\//i.test(key) && /^OFFERLETTER-/i.test(leaf)) {
      return `OFFERLETTER/OFFERLETTER-${leaf}`
    }
    return key
  }

  /** Keys mail-hr must never receive (ATS list labels / SHC- ids). */
  isForbiddenOfferLetterMailKey(value) {
    if (!value || typeof value !== 'string') return true
    const key = value.trim()
    if (!key) return true
    if (/SHC-\d+/i.test(key)) return true
    return this.isDisplayStyleOfferFilename(key)
  }

  buildGeneratedOfferLetterFilename({ candidateName, location } = {}) {
    const name = this.sanitizeOfferLetterFilenamePart(candidateName) || 'UNKNOWN'
    const loc = this.sanitizeOfferLetterFilenamePart(location) || 'UNKNOWN'
    return `OFFERLETTER-${name}-${loc}.pdf`
  }

  collectOfferLetterStoredKeys(candidate, extraDetails, extras = {}) {
    const extra = extraDetails || this.parseExtraDetails(candidate?.extra_details) || {}
    const offer = extra.offerLetter && typeof extra.offerLetter === 'object' ? extra.offerLetter : {}
    const payroll = extra.payroll_data || extra.payroll || candidate?.payroll_data || {}
    const values = [
      extras.sessionFilename,
      extras.generatedFilename,
      extra.offerLetterFilename,
      extra.s3Key,
      extra.offerLetterS3Key,
      extra.offerLetterPath,
      extra.offerLetterLocation,
      extra.s3Location,
      offer.s3Key,
      offer.offerLetterFilename,
      offer.filename,
      offer.offerLetterLocation,
      offer.s3Location,
      candidate?.offerLetterFilename,
      payroll.offerLetterPath,
    ]
    const keys = []
    const seen = new Set()
    const push = (key) => {
      if (!key || seen.has(key) || this.isForbiddenOfferLetterMailKey(key)) return
      seen.add(key)
      keys.push(key)
    }
    values.forEach((raw) => {
      if (!raw || typeof raw !== 'string') return
      const trimmed = raw.trim().replace(/^\/+/, '')
      if (!trimmed || this.isHttpOrBlobUrl(trimmed) || /^data:/i.test(trimmed)) return
      if (this.isForbiddenOfferLetterMailKey(trimmed)) return
      // Prefer folder+leaf — generate stores OFFERLETTER/{leaf}, not the bare leaf.
      if (!/[\\/]/.test(trimmed)) {
        push(this.withOfferLetterFolderPrefix(trimmed))
      }
      push(trimmed)
    })
    return keys
  }

  _looksLikePdfPayload(value) {
    if (value == null) return false
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value.size > 32
    if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) return value.byteLength > 32
    if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(value)) return value.byteLength > 32
    if (typeof value !== 'string') return false
    const trimmed = value.trim()
    if (!trimmed || trimmed.length < 32) return false
    if (/^<!DOCTYPE|^<html[\s>]|^<body[\s>]/i.test(trimmed)) return false
    if (/^https?:/i.test(trimmed) || /^blob:/i.test(trimmed)) return false
    if (trimmed.startsWith('%PDF')) return true
    if (/^data:application\/pdf/i.test(trimmed)) return true
    if (/^37,\s*80,\s*68,\s*70/.test(trimmed)) return true
    const b64 = (trimmed.includes('base64,')
      ? trimmed.slice(trimmed.indexOf('base64,') + 7)
      : trimmed).replace(/\s+/g, '')
    // %PDF in standard base64
    return /^JVBERi/.test(b64)
  }

  isFileBase64Hit(res) {
    if (!res || typeof res !== 'object') return false
    const status = res.status
    if (typeof status === 'number' && status >= 400) return false
    if (status === 404 || status === 400 || status === 500) return false
    if (res.statusText === 'Not Found') return false
    // getFileBase64 returns err.response (axios wrapper) on HTTP errors — never a file.
    if (res.headers && res.config) return false

    const nested = res.data && typeof res.data === 'object' && !Array.isArray(res.data)
      ? res.data
      : null
    const payloads = [
      res.file, res.base64, res.pdf, res.pdfBase64,
      nested?.file, nested?.base64, nested?.pdf, nested?.pdfBase64,
    ]
    return payloads.some(payload => this._looksLikePdfPayload(payload))
  }

  /**
   * Resolve a real S3 object key for mail-hr. Never returns a display/SHC- key.
   * Try order (each must return real PDF bytes from getFileBase64):
   *   1. Exact generate-pdf key as returned
   *   2. Folder + exact generate filename (OFFERLETTER/{exact})
   *   3. Stored s3Key / offerLetterPath from extra_details / status API
   * Never invent OFFERLETTER/{name}-{loc}.pdf by stripping OFFERLETTER- from the leaf.
   */
  async resolveVerifiedOfferLetterS3Key({
    candidate,
    extraDetails,
    sessionFilename,
    generatedFilename,
    getFileBase64,
    fetchExtraDetails,
  } = {}) {
    if (typeof getFileBase64 !== 'function') return null

    let extra = extraDetails || this.parseExtraDetails(candidate?.extra_details) || {}
    const seen = new Set()

    const tryKeys = async (keys) => {
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i]
        if (!key || seen.has(key) || this.isForbiddenOfferLetterMailKey(key)) continue
        seen.add(key)
        try {
          const res = await getFileBase64(key)
          const hit = this.isFileBase64Hit(res)
          if (hit) return key
        } catch (err) {
          /* try next candidate */
        }
      }
      return null
    }

    const exactGenerateKeys = []
    const pushExact = (raw) => {
      if (!raw || typeof raw !== 'string') return
      const key = raw.trim().replace(/^\/+/, '')
      if (!key || this.isHttpOrBlobUrl(key) || /^data:/i.test(key)) return
      if (this.isForbiddenOfferLetterMailKey(key)) return
      if (exactGenerateKeys.includes(key)) return
      exactGenerateKeys.push(key)
    }
    pushExact(generatedFilename)
    pushExact(sessionFilename)

    const folderPlusExact = exactGenerateKeys
      .map(key => this.withOfferLetterFolderPrefix(key))
      .filter(key => key && !exactGenerateKeys.includes(key))

    const stored = this.collectOfferLetterStoredKeys(candidate, extra)
      .filter(key => !exactGenerateKeys.includes(key) && !folderPlusExact.includes(key))

    // Folder+exact first — generate returns a leaf that is never an S3 object.
    let hit = await tryKeys([...folderPlusExact, ...exactGenerateKeys, ...stored])
    if (hit && !this.isForbiddenOfferLetterMailKey(hit)) {
      return hit
    }

    if (typeof fetchExtraDetails === 'function') {
      try {
        const fresh = await fetchExtraDetails()
        if (fresh && typeof fresh === 'object') {
          extra = { ...extra, ...fresh }
          const freshStored = this.collectOfferLetterStoredKeys(candidate, extra)
            .filter(key => !exactGenerateKeys.includes(key) && !folderPlusExact.includes(key))
          hit = await tryKeys(freshStored)
          if (hit && !this.isForbiddenOfferLetterMailKey(hit)) return hit
        }
      } catch (err) {
        /* no invented-key fallback */
      }
    }

    return null
  }

  /** Blob object URL for iframe/embed — handles base64 and comma-separated byte payloads. */
  async pdfPayloadToObjectUrl(payload) {
    const blob = await this.pdfPayloadToBlob(payload)
    if (typeof URL === 'undefined' || !URL.createObjectURL) {
      throw new Error('Browser URL.createObjectURL is not available')
    }
    return URL.createObjectURL(blob)
  }

  /**
   * Data URL for download / fallback. Do not use as an iframe src for PDFs —
   * Chrome leaves data:application/pdf iframes blank. Prefer pdfPayloadToObjectUrl.
   */
  async pdfPayloadToDataUrl(payload) {
    if (payload == null || payload === '') {
      throw new Error(`Empty PDF payload (${this._pdfPayloadPreview(payload)})`)
    }
    if (typeof payload === 'string') {
      const trimmed = payload.trim()
      if (/^data:application\/pdf(;[^,]*)?;base64,/i.test(trimmed)) {
        return trimmed
      }
    }
    const blob = await this.pdfPayloadToBlob(payload)
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result === 'string' && reader.result) {
          resolve(reader.result)
          return
        }
        reject(new Error(`Failed to convert PDF blob to data URL (${this._pdfPayloadPreview(payload)})`))
      }
      reader.onerror = () => reject(reader.error || new Error('Failed to read PDF blob as data URL'))
      reader.readAsDataURL(blob)
    })
  }

  _extractPdfErrorMessage(error) {
    const data = error?.response?.data
    if (typeof data === 'string' && data.trim()) return data.trim()
    if (data && typeof data === 'object') {
      const msg = data.message || data.error || data.msg
        || (typeof data.data === 'string' ? data.data : null)
      if (msg && typeof msg === 'string') return msg
      if (Array.isArray(data.errors) && data.errors.length) {
        return data.errors.map(e => (typeof e === 'string' ? e : e.message)).filter(Boolean).join('; ')
      }
      try {
        return JSON.stringify(data)
      } catch (e) {
        /* ignore */
      }
    }
    if (error?.message && !/^Request failed with status code \d+$/i.test(error.message)) {
      return error.message
    }
    const status = error?.response?.status
    if (status === 400) {
      return 'PDF generation rejected (400). API requires html, filename, candidate_id, and job_id.'
    }
    return error?.message || 'Failed to generate offer letter PDF'
  }
}

export default new OfferLetterService()
